import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Skeleton } from 'primeng/skeleton';
import { CARE_PLAN_FIXTURE_TAXONOMY } from '../../care-plan-fixture-taxonomy.token';
import { CarePlanStore } from '../../care-plan.store';
import { ActionCardComponent } from '../action-card/action-card.component';
import { CarePlanAction } from '../actions.model';
import { CarePlanActionsStore } from '../actions.store';

@Component({
  selector: 'bn-open-actions-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ActionCardComponent, FormsModule, Select, Skeleton],
  templateUrl: './open-actions-list.component.html',
  styleUrl: './open-actions-list.component.scss',
})
export class OpenActionsListComponent {
  private readonly _carePlanStore = inject(CarePlanStore);
  private readonly _fixtureTaxonomy = inject(CARE_PLAN_FIXTURE_TAXONOMY, {
    optional: true,
  });

  readonly store = input.required<CarePlanActionsStore>();
  readonly goToCase = output<string>();
  readonly ownerFilter = signal('');
  readonly caseFilter = signal('');
  readonly hasFilters = computed(
    () => !!(this.ownerFilter() || this.caseFilter()),
  );
  readonly filteredActions = computed(() =>
    this.store()
      .actions()
      .filter(
        (action) =>
          (!this.ownerFilter() ||
            (action.assignedToId ?? '__none') === this.ownerFilter()) &&
          (!this.caseFilter() ||
            (action.caseId ?? '__none') === this.caseFilter()),
      ),
  );
  readonly ownerOptions = computed(() => [
    { label: 'All assigned FAs', value: '' },
    ...[
      ...new Set(
        this.store()
          .actions()
          .map((a) => a.assignedToId),
      ),
    ]
      .map((id) => ({
        label: id ? this.store().ownerLabel(id) : 'Unassigned',
        value: id ?? '__none',
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]);
  readonly caseOptions = computed(() => [
    { label: 'All cases', value: '' },
    { label: 'No case', value: '__none' },
    ...[
      ...new Set(
        this.store()
          .actions()
          .map((a) => a.caseId)
          .filter((id): id is string => !!id),
      ),
    ]
      .map((id) => ({
        label:
          this._carePlanStore.cases().find((c) => c.id === id)?.title ??
          `Case #${id}`,
        value: id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]);

  resetFilters(): void {
    this.ownerFilter.set('');
    this.caseFilter.set('');
  }

  protected readonly readonly = computed(
    () =>
      this._carePlanStore.isLive() && !this._carePlanStore.canEditLiveCases(),
  );

  protected hasLinkedCase(action: CarePlanAction): boolean {
    return !!this._case(action);
  }

  protected caseDomainLabel(action: CarePlanAction): string | null {
    const linkedCase = this._case(action);
    if (!linkedCase) return null;
    if (linkedCase.domainId != null) {
      const domain = this._carePlanStore
        .domains()
        .find((item) => item.domainId === linkedCase.domainId);
      return domain?.shortName || domain?.name || 'Case domain unavailable';
    }
    return (
      this._fixtureTaxonomy?.domains.find(
        (domain) => domain.key === linkedCase.domain,
      )?.label ?? linkedCase.domain
    );
  }

  private _case(action: CarePlanAction) {
    return action.caseId
      ? this._carePlanStore.cases().find((item) => item.id === action.caseId)
      : undefined;
  }
}
